import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Calculator as CalculatorIcon,
  StickyNote,
  ListChecks,
  Ruler,
  Percent,
  Package,
  PhoneCall,
  Trash2,
  Pin,
  PinOff,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// ---------- Calculator ----------
const Calculator = () => {
  const [expr, setExpr] = useState("");
  const [result, setResult] = useState("");
  const evalSafe = (s) => {
    // Allow only numbers, operators, parens, %, .
    if (!/^[\d+\-*/().% ]*$/.test(s)) return "";
    try {
      // convert 12% to (12/100) contextually — simplest: replace X% with (X/100)
      const normalized = s.replace(/(\d+(?:\.\d+)?)%/g, "($1/100)");
       
      const v = Function("return " + normalized)();
      if (typeof v === "number" && !Number.isNaN(v)) return Number(v.toFixed(6)).toString();
    } catch {
      return "";
    }
    return "";
  };
  useEffect(() => setResult(evalSafe(expr)), [expr]);
  return (
    <div className="space-y-3">
      <Input
        data-testid="qt-calc-expr"
        placeholder="e.g. (1200*1.18)"
        value={expr}
        onChange={(e) => setExpr(e.target.value)}
        className="rounded-lg text-lg font-mono"
      />
      <div className="rounded-lg bg-[#F1FAEE] border border-[#E4E7EC] p-4 text-right">
        <div className="text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold">Result</div>
        <div className="text-3xl font-bold text-[#1d3557] font-mono" data-testid="qt-calc-result">
          {result || "0"}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PresetBtn label="+ 18% GST" onClick={() => setExpr((v) => (v ? `(${v})*1.18` : ""))} />
        <PresetBtn label="− 18% GST" onClick={() => setExpr((v) => (v ? `(${v})/1.18` : ""))} />
        <PresetBtn label="+ 5% GST" onClick={() => setExpr((v) => (v ? `(${v})*1.05` : ""))} />
        <PresetBtn label="− 5% GST" onClick={() => setExpr((v) => (v ? `(${v})/1.05` : ""))} />
      </div>
    </div>
  );
};
const PresetBtn = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="h-10 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] hover:bg-[#F1FAEE]"
  >
    {label}
  </button>
);

// ---------- GST Helper ----------
const GstHelper = () => {
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("18");
  const [mode, setMode] = useState("exclusive"); // exclusive | inclusive
  const a = parseFloat(amount) || 0;
  const r = parseFloat(rate) || 0;
  let base = 0, tax = 0, total = 0;
  if (mode === "exclusive") {
    base = a;
    tax = a * (r / 100);
    total = base + tax;
  } else {
    total = a;
    base = a / (1 + r / 100);
    tax = total - base;
  }
  const cgst = tax / 2;
  const sgst = tax / 2;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Amount</Label>
          <Input data-testid="qt-gst-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-md mt-1" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">GST %</Label>
          <Input data-testid="qt-gst-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-md mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="qt-gst-mode-excl"
          onClick={() => setMode("exclusive")}
          className={`h-10 rounded-md text-[12px] font-semibold border ${
            mode === "exclusive" ? "bg-[#1d3557] text-white border-[#1d3557]" : "bg-white text-[#1d3557] border-[#E4E7EC]"
          }`}
        >
          Add GST (excl.)
        </button>
        <button
          data-testid="qt-gst-mode-incl"
          onClick={() => setMode("inclusive")}
          className={`h-10 rounded-md text-[12px] font-semibold border ${
            mode === "inclusive" ? "bg-[#1d3557] text-white border-[#1d3557]" : "bg-white text-[#1d3557] border-[#E4E7EC]"
          }`}
        >
          Remove GST (incl.)
        </button>
      </div>
      <div className="rounded-lg bg-[#F1FAEE] border border-[#E4E7EC] p-4 space-y-1.5 font-mono text-[13px]">
        <Row label="Base" value={base} />
        <Row label={`CGST ${(r / 2).toFixed(1)}%`} value={cgst} />
        <Row label={`SGST ${(r / 2).toFixed(1)}%`} value={sgst} />
        <div className="border-t border-[#E4E7EC] my-1" />
        <Row label="Total" value={total} strong />
      </div>
    </div>
  );
};
const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between ${strong ? "font-bold text-[#1d3557]" : "text-[#457b9d]"}`}>
    <span>{label}</span>
    <span className="text-[#1d3557]">₹ {value.toFixed(2)}</span>
  </div>
);

// ---------- Unit Converter ----------
const UNITS = [
  { from: "mm", to: "inch", factor: 0.0393701 },
  { from: "inch", to: "mm", factor: 25.4 },
  { from: "litre", to: "ml", factor: 1000 },
  { from: "ml", to: "litre", factor: 0.001 },
  { from: "kg", to: "gram", factor: 1000 },
  { from: "gram", to: "kg", factor: 0.001 },
  { from: "PSI", to: "bar", factor: 0.0689476 },
  { from: "bar", to: "PSI", factor: 14.5038 },
  { from: "km", to: "mile", factor: 0.621371 },
  { from: "mile", to: "km", factor: 1.60934 },
];
const UnitConverter = () => {
  const [sel, setSel] = useState(0);
  const [val, setVal] = useState("");
  const u = UNITS[sel];
  const n = parseFloat(val) || 0;
  const out = n * u.factor;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto p-1">
        {UNITS.map((x, i) => (
          <button
            key={`${x.from}-${x.to}`}
            onClick={() => setSel(i)}
            data-testid={`qt-unit-${x.from}-${x.to}`}
            className={`h-9 rounded-md text-[11px] font-semibold border ${
              i === sel ? "bg-[#1d3557] text-white border-[#1d3557]" : "bg-white text-[#1d3557] border-[#E4E7EC]"
            }`}
          >
            {x.from}→{x.to}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="qt-unit-val" type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder={u.from} className="rounded-md" />
        <div className="rounded-md bg-[#F1FAEE] border border-[#E4E7EC] px-3 grid place-items-center font-mono text-[15px] text-[#1d3557]">
          {out ? out.toFixed(4) : "—"} {u.to}
        </div>
      </div>
    </div>
  );
};

// ---------- Notes ----------
const NOTE_COLORS = { yellow: "#FEF3C7", blue: "#DBEAFE", green: "#DCFCE7", pink: "#FCE7F3", gray: "#F3F4F6" };
const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState({ body: "", color: "yellow" });
  const load = async () => {
    try {
      const { data } = await api.get("/notes");
      setNotes(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!draft.body.trim()) return;
    try {
      const { data } = await api.post("/notes", draft);
      setNotes((prev) => [data, ...prev]);
      setDraft({ body: "", color: "yellow" });
    } catch { toast.error("Failed to add note"); }
  };
  const togglePin = async (n) => {
    try {
      const { data } = await api.patch(`/notes/${n.id}`, { pinned: !n.pinned });
      setNotes((prev) => [...prev.map((x) => (x.id === n.id ? data : x))].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)));
    } catch { /* ignore */ }
  };
  const remove = async (n) => {
    try {
      await api.delete(`/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch { toast.error("Failed to delete"); }
  };
  return (
    <div className="space-y-3">
      <textarea
        data-testid="qt-note-body"
        placeholder="Type a quick note…"
        rows={2}
        value={draft.body}
        onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
        className="w-full rounded-md border border-[#E4E7EC] px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#457b9d]"
      />
      <div className="flex items-center gap-1.5 flex-wrap">
        {Object.keys(NOTE_COLORS).map((c) => (
          <button
            key={c}
            onClick={() => setDraft((d) => ({ ...d, color: c }))}
            data-testid={`qt-note-color-${c}`}
            className={`w-6 h-6 rounded-full border-2 ${draft.color === c ? "border-[#1d3557]" : "border-[#E4E7EC]"}`}
            style={{ background: NOTE_COLORS[c] }}
          />
        ))}
        <Button data-testid="qt-note-add" onClick={create} className="ml-auto h-8 px-3 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white text-[12px] font-semibold">
          Add
        </Button>
      </div>
      <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-1">
        {notes.length === 0 && <div className="text-[12px] text-[#667085] text-center py-6">No notes yet.</div>}
        {notes.map((n) => (
          <div
            key={n.id}
            data-testid={`qt-note-${n.id}`}
            className="rounded-md p-3 border border-[#E4E7EC] text-[13px] text-[#1d3557]"
            style={{ background: NOTE_COLORS[n.color] || NOTE_COLORS.yellow }}
          >
            <div className="whitespace-pre-wrap">{n.body}</div>
            <div className="mt-2 flex items-center justify-end gap-1">
              <button onClick={() => togglePin(n)} title={n.pinned ? "Unpin" : "Pin"} className="p-1 hover:text-[#e63946]">
                {n.pinned ? <PinOff size={13} /> : <Pin size={13} />}
              </button>
              <button onClick={() => remove(n)} className="p-1 hover:text-[#e63946]">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- To-Do ----------
const Todos = () => {
  const [todos, setTodos] = useState([]);
  const [draft, setDraft] = useState({ title: "", priority: "normal" });
  const load = async () => {
    try {
      const { data } = await api.get("/todos");
      setTodos(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!draft.title.trim()) return;
    try {
      const { data } = await api.post("/todos", draft);
      setTodos((prev) => [data, ...prev]);
      setDraft({ title: "", priority: "normal" });
    } catch { toast.error("Failed to add task"); }
  };
  const toggle = async (t) => {
    try {
      const { data } = await api.patch(`/todos/${t.id}`, { done: !t.done });
      setTodos((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    } catch { /* ignore */ }
  };
  const remove = async (t) => {
    try {
      await api.delete(`/todos/${t.id}`);
      setTodos((prev) => prev.filter((x) => x.id !== t.id));
    } catch { toast.error("Failed to delete"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          data-testid="qt-todo-title"
          placeholder="Add a task…"
          value={draft.title}
          onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && create()}
          className="rounded-md"
        />
        <select
          data-testid="qt-todo-priority"
          value={draft.priority}
          onChange={(e) => setDraft((d) => ({ ...d, priority: e.target.value }))}
          className="rounded-md border border-[#E4E7EC] px-2 text-[12px] font-semibold bg-white"
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
        </select>
        <Button data-testid="qt-todo-add" onClick={create} className="h-10 px-3 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white text-[12px] font-semibold">Add</Button>
      </div>
      <div className="space-y-1.5 max-h-[48vh] overflow-y-auto pr-1">
        {todos.length === 0 && <div className="text-[12px] text-[#667085] text-center py-6">No tasks yet.</div>}
        {todos.map((t) => (
          <div
            key={t.id}
            data-testid={`qt-todo-${t.id}`}
            className={`flex items-center gap-2 p-2.5 rounded-md border border-[#E4E7EC] ${t.done ? "bg-[#F1FAEE] opacity-60" : "bg-white"}`}
          >
            <input
              type="checkbox"
              checked={!!t.done}
              onChange={() => toggle(t)}
              className="w-4 h-4 rounded"
              data-testid={`qt-todo-check-${t.id}`}
            />
            <div className={`flex-1 text-[13px] ${t.done ? "line-through text-[#667085]" : "text-[#1d3557]"}`}>{t.title}</div>
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                t.priority === "high" ? "bg-[#e63946]/15 text-[#e63946]" :
                t.priority === "low" ? "bg-[#F1FAEE] text-[#457b9d]" : "bg-[#a8dadc]/40 text-[#1d3557]"
              }`}
            >
              {t.priority}
            </span>
            <button onClick={() => remove(t)} className="p-1 text-[#667085] hover:text-[#e63946]">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Quick Part Lookup ----------
const PartLookup = () => {
  const [q, setQ] = useState("");
  const [parts, setParts] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    if (!q.trim()) { setParts([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/parts", { params: { q } });
        setParts(data || []);
      } catch { setParts([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-3">
      <Input data-testid="qt-part-search" placeholder="Part name or SKU…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-md" />
      <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1">
        {q && parts.length === 0 && <div className="text-[12px] text-[#667085] text-center py-6">No parts found.</div>}
        {parts.map((p) => (
          <div key={p.id} className="p-2.5 rounded-md border border-[#E4E7EC] bg-white flex items-center gap-2">
            <Package size={16} className="text-[#457b9d]" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#1d3557] truncate">{p.name}</div>
              <div className="text-[11px] text-[#667085]">SKU {p.sku} · ₹ {(p.selling_price || 0).toFixed(2)} · stock {p.stock_qty || 0}</div>
            </div>
            <Button
              size="sm"
              onClick={() => nav("/inventory")}
              className="h-7 px-2 bg-[#1d3557] hover:bg-[#152941] text-white text-[11px] rounded-md"
              data-testid={`qt-part-open-${p.id}`}
            >
              Open
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Quick Contact ----------
const QuickContact = () => {
  const { garage } = useAuth();
  const items = [
    { label: "SparesHub Sales", phone: "+91-9422799760", note: "For parts, catalogues and enquiries" },
    { label: "Fender Support", phone: "+91-9422799760", note: "Product help and how-tos" },
    { label: "Your garage", phone: garage?.phone || "Set in Team → Settings", note: garage?.name || "" },
  ];
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.label} className="rounded-md p-3 border border-[#E4E7EC] bg-white">
          <div className="text-[13px] font-semibold text-[#1d3557]">{c.label}</div>
          <div className="text-[11px] text-[#667085] mb-2">{c.note}</div>
          <div className="flex items-center gap-2">
            <a
              href={`tel:${c.phone.replace(/[^+\d]/g, "")}`}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152941]"
              data-testid={`qt-contact-call-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <PhoneCall size={13} /> Call
            </a>
            <a
              href={`https://wa.me/${c.phone.replace(/[^+\d]/g, "").replace(/^\+/, "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#12B76A] text-white text-[12px] font-semibold hover:opacity-90"
            >
              WhatsApp
            </a>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- Main Drawer ----------
const TABS = [
  { k: "calc", label: "Calc", Icon: CalculatorIcon, Panel: Calculator },
  { k: "gst", label: "GST", Icon: Percent, Panel: GstHelper },
  { k: "notes", label: "Notes", Icon: StickyNote, Panel: Notes },
  { k: "todo", label: "To-Do", Icon: ListChecks, Panel: Todos },
  { k: "conv", label: "Units", Icon: Ruler, Panel: UnitConverter },
  { k: "part", label: "Parts", Icon: Package, Panel: PartLookup },
  { k: "call", label: "Contact", Icon: PhoneCall, Panel: QuickContact },
];

export default function QuickToolsDrawer({ open, onOpenChange }) {
  const [tab, setTab] = useState("calc");
  const Active = TABS.find((t) => t.k === tab)?.Panel || Calculator;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="w-full sm:w-[420px] p-0 bg-white border-l border-[#E4E7EC] flex flex-col"
        data-testid="quick-tools-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b border-[#E4E7EC]">
          <SheetTitle className="text-[16px] font-semibold text-[#1d3557]">Quick Tools</SheetTitle>
        </SheetHeader>
        <div className="px-3 pt-3 border-b border-[#E4E7EC] overflow-x-auto no-scrollbar">
          <div className="inline-flex gap-1 pb-2">
            {TABS.map(({ k, label, Icon }) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`qt-tab-${k}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-semibold whitespace-nowrap transition-colors ${
                  tab === k ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557] hover:bg-[#F1FAEE]"
                }`}
              >
                <Icon size={13} strokeWidth={2} />
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <Active />
        </div>
      </SheetContent>
    </Sheet>
  );
}
